import { create } from 'zustand';
import type { VoiceReceiveMode } from './voice.js';

/**
 * Player settings. Persisted to localStorage and applied to the game runtime
 * imperatively — the engine subscribes once and pushes values into the input
 * manager, the audio engine and the renderer. Nothing here is read per frame,
 * so React stays out of the game loop as usual.
 */
export interface Settings {
  /** 0–1, applied to the audio engine's master gain. */
  masterVolume: number;
  /**
   * In "sensitivity units", the number players actually recognise and copy
   * between games. Multiplied by 0.001 to get radians per unit of mouse travel.
   */
  sensitivity: number;
  /** Multiplier applied while aiming down sights. */
  adsSensitivityScale: number;
  invertY: boolean;
  /** Horizontal field of view in degrees, before any ADS zoom. */
  fieldOfView: number;
  /** Show the ping and framerate readout on the HUD. */
  showStats: boolean;
  /** Touch look sensitivity, in the same 0.2–10 "units" scale as the mouse. */
  touchSensitivity: number;
  /**
   * Fire automatically when the crosshair is on an enemy — the mobile-shooter
   * default (CoD Mobile "simple mode" and its peers). Touch devices only.
   */
  touchAutoFire: boolean;
  /**
   * Who is audible over voice chat. Only the *incoming* side persists — the
   * microphone is session state that always starts muted (see game store).
   */
  voiceReceive: VoiceReceiveMode;
}

export const DEFAULT_SETTINGS: Settings = {
  masterVolume: 0.7,
  sensitivity: 2.2,
  adsSensitivityScale: 0.7,
  invertY: false,
  fieldOfView: 90,
  showStats: true,
  touchSensitivity: 4,
  touchAutoFire: true,
  voiceReceive: 'everyone',
};

export const VOICE_RECEIVE_MODES: readonly VoiceReceiveMode[] = [
  'everyone',
  'team',
  'off',
];

/** Radians of view rotation per unit of raw mouse movement, per sensitivity unit. */
export const SENSITIVITY_UNIT = 0.001;

/**
 * Radians per pixel of touch drag, per sensitivity unit. Higher than the mouse
 * unit because a thumb swipe covers far fewer pixels than a mouse flick.
 */
export const TOUCH_SENSITIVITY_UNIT = 0.0015;

export const LIMITS = {
  masterVolume: { min: 0, max: 1, step: 0.01 },
  sensitivity: { min: 0.2, max: 10, step: 0.1 },
  adsSensitivityScale: { min: 0.2, max: 1.5, step: 0.05 },
  fieldOfView: { min: 70, max: 120, step: 1 },
  touchSensitivity: { min: 0.2, max: 10, step: 0.1 },
} as const;

const STORAGE_KEY = 'callout:settings';

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Turns whatever was persisted into usable settings, falling back per-key rather
 * than wholesale: a file written by an older build is still worth most of its
 * contents, and a corrupt or hand-edited one must not stop the game from
 * starting or let an out-of-range value through to the renderer.
 */
export const parseSettings = (stored: unknown): Settings => {
  const settings = { ...DEFAULT_SETTINGS };
  if (typeof stored !== 'object' || stored === null) return settings;
  const raw = stored as Partial<Record<keyof Settings, unknown>>;

  for (const key of [
    'masterVolume',
    'sensitivity',
    'adsSensitivityScale',
    'fieldOfView',
    'touchSensitivity',
  ] as const) {
    const value = raw[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      settings[key] = clamp(value, LIMITS[key].min, LIMITS[key].max);
    }
  }
  for (const key of ['invertY', 'showStats', 'touchAutoFire'] as const) {
    if (typeof raw[key] === 'boolean') settings[key] = raw[key];
  }
  if (
    typeof raw.voiceReceive === 'string' &&
    (VOICE_RECEIVE_MODES as readonly string[]).includes(raw.voiceReceive)
  ) {
    settings.voiceReceive = raw.voiceReceive as VoiceReceiveMode;
  }
  return settings;
};

const load = (): Settings => {
  try {
    return parseSettings(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null'));
  } catch {
    // Unparseable JSON, or no localStorage at all (server-side, or a locked-down
    // browser). Defaults are always a working answer.
    return { ...DEFAULT_SETTINGS };
  }
};

const save = (settings: Settings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Private browsing and full quotas both throw here. Losing persistence is
    // not a reason to break the settings panel.
  }
};

interface SettingsStore extends Settings {
  update(patch: Partial<Settings>): void;
  reset(): void;
}

export const useSettingsStore = create<SettingsStore>((set, get) => ({
  ...load(),

  update: (patch) => {
    set(patch);
    save(current(get()));
  },

  reset: () => {
    set({ ...DEFAULT_SETTINGS });
    save({ ...DEFAULT_SETTINGS });
  },
}));

/** Strips the actions, leaving the persistable values. */
const current = (state: SettingsStore): Settings => ({
  masterVolume: state.masterVolume,
  sensitivity: state.sensitivity,
  adsSensitivityScale: state.adsSensitivityScale,
  invertY: state.invertY,
  fieldOfView: state.fieldOfView,
  showStats: state.showStats,
  touchSensitivity: state.touchSensitivity,
  touchAutoFire: state.touchAutoFire,
  voiceReceive: state.voiceReceive,
});

export const settingsSnapshot = (): Settings => current(useSettingsStore.getState());

/** Non-reactive access for the engine, which must not subscribe as a component. */
export const settingsStore = useSettingsStore;
